// ----------------------------------------------------------------------------
// knob — Shared rotary knob control for DAWSEUR panels
//
// Drag vertically to change value. Double-click resets to default.
// Wheel to fine-tune (shift+wheel for larger steps).
// Right-click for context menu: Automate, Reset, Link to controller.
// ----------------------------------------------------------------------------

// ---- Minimal context menu helper (no external dependency) ----
let _activeContextMenu = null;

function showKnobContextMenu(e, items) {
   e.preventDefault();
   e.stopPropagation();
   closeKnobContextMenu();

   const menu = document.createElement('div');
   menu.className = 'lw-knob-menu';
   menu.style.cssText = 'position:fixed;z-index:99999;background:var(--c1);border:1px solid var(--c4);border-radius:3px;box-shadow:0 4px 12px rgba(0,0,0,0.5);font-family:bit,monospace;font-size:11px;min-width:130px;padding:2px 0;';

   for (const item of items) {
      if (item === 'hr' || item.isDivider) {
         const hr = document.createElement('div');
         hr.style.cssText = 'height:1px;background:var(--c3);margin:2px 0;';
         menu.appendChild(hr);
         continue;
      }
      const row = document.createElement('div');
      row.textContent = item.label;
      row.style.cssText = 'padding:4px 12px;cursor:pointer;color:var(--ce);white-space:nowrap;';
      row.onmouseenter = () => { row.style.background = 'var(--c4)'; };
      row.onmouseleave = () => { row.style.background = ''; };
      row.onclick = (ev) => {
         ev.stopPropagation();
         closeKnobContextMenu();
         if (item.callback) item.callback();
      };
      menu.appendChild(row);
   }

   // Position at cursor, clamped to viewport
   let x = e.clientX, y = e.clientY;
   document.body.appendChild(menu);
   const rect = menu.getBoundingClientRect();
   if (x + rect.width > window.innerWidth) x = window.innerWidth - rect.width - 4;
   if (y + rect.height > window.innerHeight) y = window.innerHeight - rect.height - 4;
   menu.style.left = x + 'px';
   menu.style.top = y + 'px';

   _activeContextMenu = menu;

   // Close on any click outside or escape
   setTimeout(() => {
      document.addEventListener('mousedown', _closeOnOutside, true);
      document.addEventListener('contextmenu', _closeOnOtherMenu, true);
   }, 0);
}

function closeKnobContextMenu() {
   if (_activeContextMenu) {
      _activeContextMenu.remove();
      _activeContextMenu = null;
      document.removeEventListener('mousedown', _closeOnOutside, true);
      document.removeEventListener('contextmenu', _closeOnOtherMenu, true);
   }
}

function _closeOnOutside(e) {
   if (_activeContextMenu && !_activeContextMenu.contains(e.target)) {
      closeKnobContextMenu();
   }
}

function _closeOnOtherMenu(e) {
   if (_activeContextMenu) {
      closeKnobContextMenu();
   }
}

function makeKnob(label, min, max, step, value, onChange, formatFn, opts) {
   opts = opts || {};
   const wrap = document.createElement('div');
   wrap.className = 'lw-mx-knob';

   const dial = document.createElement('div');
   dial.className = 'lw-mx-knob-dial';

   const indicator = document.createElement('div');
   indicator.className = 'lw-mx-knob-indicator';
   dial.appendChild(indicator);

   const lab = document.createElement('div');
   lab.className = 'lw-mx-knob-label';
   lab.textContent = label;
   wrap.appendChild(dial);
   if (!opts.noLabels) wrap.appendChild(lab);

   const val = document.createElement('div');
   val.className = 'lw-mx-knob-val';
   if (!opts.noLabels) wrap.appendChild(val);

   const defaultValue = value;
   let current = value;

   const ANGLE_MIN = -135;
   const ANGLE_MAX = 135;
   const range = max - min;

   const defaultFormat = (v) => {
      if (label === 'PAN') return v === 0 ? 'C' : (v < 0 ? 'L' + Math.round(-v) : 'R' + Math.round(v));
      if (label === 'VOL') return Math.round(v * 100) + '%';
      return (v > 0 ? '+' : '') + v.toFixed(1);
   };
   const formatVal = formatFn || defaultFormat;

   function update() {
      const norm = (current - min) / range;
      const angle = ANGLE_MIN + norm * (ANGLE_MAX - ANGLE_MIN);
      dial.style.transform = `rotate(${angle}deg)`;
      val.textContent = formatVal(current);
   }

   function setValue(v, silent) {
      current = Math.max(min, Math.min(max, v));
      current = Math.round(current / step) * step;
      update();
      if (onChange && !silent) onChange(current);
   }

   // Update visual only (no onChange callback) — used by automation playback
   function setValueVisual(v) {
      setValue(v, true);
   }

   update();

   let dragging = false;
   let startY = 0;
   let startVal = 0;

   dial.addEventListener('mousedown', (e) => {
      if (e.button === 2) return; // right-click: let contextmenu handle it
      dragging = true;
      startY = e.clientY;
      startVal = current;
      e.preventDefault();
      document.body.style.cursor = 'ns-resize';
   });

   const mouseMoveHandler = (e) => {
      if (!dragging) return;
      const dy = startY - e.clientY;
      const sensitivity = 150;
      const delta = (dy / sensitivity) * range;
      setValue(startVal + delta);
   };

   const mouseUpHandler = () => {
      if (dragging) {
         dragging = false;
         document.body.style.cursor = '';
      }
   };

   document.addEventListener('mousemove', mouseMoveHandler);
   document.addEventListener('mouseup', mouseUpHandler);

   dial.addEventListener('dblclick', () => {
      setValue(defaultValue);
   });

   dial.addEventListener('wheel', (e) => {
      e.preventDefault();
      const dir = e.deltaY < 0 ? 1 : -1;
      setValue(current + dir * step * (e.shiftKey ? 10 : 1));
   }, { passive: false });

   // ---- Context menu (right-click) ----
   wrap.oncontextmenu = (e) => {
      const menuItems = [];
      if (typeof window !== 'undefined' && window.createAutomation) {
         menuItems.push({
            label: 'Automate',
            callback: () => {
               window.createAutomation({
                  label: label,
                  min: min,
                  max: max,
                  step: step,
                  getValue: () => current,
                  setValue: setValueVisual,
                  element: wrap,
                  knobId: knobId,
                  standalone: true,
               });
            },
         });
         menuItems.push('hr');
      }
      menuItems.push({
         label: 'Reset',
         callback: () => { setValue(defaultValue); },
      });
      menuItems.push({
         label: 'Link to controller',
         callback: () => {
            if (typeof window !== 'undefined') {
               window._knobMidiLearn = { setValue, getValue: () => current, label, element: wrap };
               if (window._knobMidiLearnCallback) window._knobMidiLearnCallback(window._knobMidiLearn);
            }
         },
      });
      menuItems.push({
         label: 'Unlink',
         callback: () => {
            if (typeof window !== 'undefined' && window._knobMidiLinks) {
               delete window._knobMidiLinks[wrap.dataset.knobId];
            }
         },
      });
      showKnobContextMenu(e, menuItems);
   };

   // Expose setValue for external updates
   wrap.setValue = setValue;
   wrap.setValueVisual = setValueVisual;
   wrap.getValue = () => current;

   // Register knob in global registry for automation playback
   // Use a deterministic ID if provided (so re-renders preserve the registry entry)
   const knobId = opts.id || ('knob_' + Math.random().toString(36).slice(2, 11));
   wrap.dataset.knobId = knobId;
   if (typeof window !== 'undefined') {
      if (!window._knobRegistry) window._knobRegistry = new Map();
      window._knobRegistry.set(knobId, { setValueVisual, getValue: () => current, label });
   }

   // Cleanup
   wrap.destroy = () => {
      document.removeEventListener('mousemove', mouseMoveHandler);
      document.removeEventListener('mouseup', mouseUpHandler);
      if (typeof window !== 'undefined' && window._knobRegistry) {
         window._knobRegistry.delete(knobId);
      }
   };

   return wrap;
}

module.exports = { makeKnob, showKnobContextMenu };
